import { useMemo, useState, useEffect, RefObject } from 'react'
import { marginBottoms, marginTops, roundeds, shadows } from './misc'
import { Rounded, Shadow } from '@bm-js/atlascloud-shared'
import { getPageBackground } from './color.util'

export const useMargin = (content: any) => {
  const gridMargin = useMemo(() => {
    const mb = marginBottoms[content?.data?.marginBottom] || ''
    const mt = marginTops[content?.data?.marginTop] || ''
    return ` ${mb} ${mt} `
  }, [content?.data?.marginBottom, content?.data?.marginTop])

  return gridMargin
}

export const useImageOptions = (content: any) => {
  const imageOptions = useMemo(() => {
    const roundedValue = (content?.data?.rounded || '') as Rounded
    const shadowValue = (content?.data?.shadow || '') as Shadow
    const rounded = roundeds[roundedValue] || ''
    const shadow = shadows[shadowValue] || ''

    return `${rounded} ${shadow}`
  }, [content.data.rounded, content.data.shadow])

  return imageOptions
}

const SCROLL_THRESHOLD = 100
export const useScroll = (element?: Element) => {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    if (!element) return
    if (typeof element === 'undefined') return
    const handleScroll = () => {
      if (element.scrollTop > SCROLL_THRESHOLD) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    element.addEventListener('scroll', handleScroll)
    return () => {
      element.removeEventListener('scroll', handleScroll)
    }
  }, [element])

  return { isScrolled }
}

// TODO: probably remove after app router migration
export const useBackground = (value?: string, asFill?: boolean) => {
  const bg = useMemo(() => {
    return getPageBackground(value, asFill)
  }, [value])

  return bg
}

export const usePageWidth = (value?: string) => {
  const pageWidth = useMemo(() => {
    const options = ['normal', 'wide']
    const classes = ['max-w-[1000px]', 'max-w-[1200px]']
    let index = options.findIndex((opt) => opt === value)
    if (index === -1) index = 0

    return `${classes[index]} mx-auto`
  }, [value])

  return pageWidth
}

interface IntersectionObserverArgs extends IntersectionObserverInit {
  freezeOnceVisible?: boolean
}

export function useIntersectionObserver(
  elementRef: RefObject<Element | null>,
  {
    threshold = 0,
    root = null,
    rootMargin = '0%',
    freezeOnceVisible = false,
  }: IntersectionObserverArgs
): IntersectionObserverEntry | undefined {
  const [entry, setEntry] = useState<IntersectionObserverEntry>()

  const frozen = entry?.isIntersecting && freezeOnceVisible

  const updateEntry = ([entry]: IntersectionObserverEntry[]): void => {
    setEntry(entry)
  }

  useEffect(() => {
    const node = elementRef?.current
    const hasIOSupport = !!window.IntersectionObserver

    if (!hasIOSupport || frozen || !node) return

    const observerParams = { threshold, root, rootMargin }
    const observer = new IntersectionObserver(updateEntry, observerParams)

    observer.observe(node)

    return () => observer.disconnect()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef?.current, JSON.stringify(threshold), root, rootMargin, frozen])

  return entry
}
